import PropTypes from "prop-types";
import React, { memo, useEffect, useMemo } from "react";

// Helpers
import { groupsBySearchCriteria } from "@/Common/helpers/Search";

// Components
import AccessListsItemBase from "../AccessListsItem";

const AccessListsItem = memo(AccessListsItemBase);

// MUI
import { Checkbox, FormControlLabel, Grid, Stack, Typography, Divider } from "@mui/material";

const getSearchCriteria = (type) => {
	switch (type) {
		case "groups":
			return {
				root: null,
				nodes: ["groupName", "groupDescription"],
			};
		case "users":
			return {
				root: "position",
				nodes: ["fullName", "position", "locationName"],
			};
		case "actions":
			return {
				root: "category",
				nodes: ["actionName", "actionDescription"],
			};
		default:
			break;
	}
	return null;
};

const getRelativeItems = (data = [], items = []) => data.filter((element) => items.includes(element._id));

const getGlobalStatus = (unfilteredData = [], args = null) => {
	const data = unfilteredData.filter((element) => !element.filteredOut);
	const { checked = [], selections = [] } = args || {};

	if (!data?.length) {
		return {
			checked: false,
			indeterminate: false,
			disabled: false,
		};
	}

	const total = data.length;
	const checkedItems = getRelativeItems(data, checked);
	const selectedItems = getRelativeItems(data, selections);
	const totalMarked = checkedItems.length + selectedItems.length;
	const isAllChecked = Number(total) === Number(totalMarked);

	return {
		checked: isAllChecked,
		indeterminate: !isAllChecked && Boolean(checkedItems.length || selectedItems.length),
		disabled: Number(total) === Number(selectedItems.length),
	};
};

const RenderListItems = (props) => {
	const {
		title,
		data,
		titleField,
		descriptionField,
		checked,
		selections,
		handleChangeCheckbox = () => {},
		handleChangeAreaCheckbox = () => {},
		intersectionRef = null,
		filteredOut = false,
		enableGroupCounter = false,
	} = props;

	const { checked: checkboxChecked, indeterminate, disabled } = getGlobalStatus(data, { checked, selections });
	const groupItemsCount = enableGroupCounter ? ` (${data?.filter((item) => !item.filteredOut).length})` : "";
	const groupLabelText = `${title}${groupItemsCount}`;

	return (
		<Grid item xs={12} mb={2} className={filteredOut ? "hidden" : ""}>
			<Stack pl={1.4} mb={1} className="relative top-0 z-10">
				<FormControlLabel
					label={<Typography className={"regular-montserrat-label fs-18"}>{groupLabelText}</Typography>}
					control={
						<Checkbox
							checked={checkboxChecked}
							indeterminate={indeterminate}
							disabled={disabled}
							onChange={(e) => handleChangeAreaCheckbox?.(e, title)}
						/>
					}
				/>
			</Stack>

			<Grid container alignContent={"flex-start"} spacing={3}>
				{data?.map(({ filteredOut: itemFilteredOut, ...item }, index) => {
					const isDisabled = selections.includes(item._id);
					const isChecked = checked.includes(item._id);
					const itemKey = `actions_item_${title}_${item._id}_${index}`;
					return (
						<Grid key={itemKey} item xs={6} md={4} className={itemFilteredOut ? "hidden" : ""}>
							<AccessListsItem
								intersectionRef={intersectionRef}
								filteredOut={itemFilteredOut}
								index={index}
								item={item}
								identifier={"_id"}
								title={titleField}
								content={descriptionField}
								checked={isChecked}
								disabled={isDisabled || item.forceDisabled}
								onActionClick={handleChangeCheckbox}
								onActionSelectCheckbox={handleChangeCheckbox}
								icon={item.icon}
							/>
						</Grid>
					);
				})}
			</Grid>
		</Grid>
	);
};
RenderListItems.propTypes = {
	title: PropTypes.string,
	data: PropTypes.array,
	titleField: PropTypes.string,
	descriptionField: PropTypes.string,
	checked: PropTypes.array,
	selections: PropTypes.array,
	handleChangeCheckbox: PropTypes.func,
	handleChangeAreaCheckbox: PropTypes.func,
	intersectionRef: PropTypes.object,
	filteredOut: PropTypes.bool,
	enableGroupCounter: PropTypes.bool,
};

export default function AccessListsContent(props) {
	const {
		data = [],
		checked = [],
		selections = [],
		accessType = "",
		titleField = "",
		descriptionField = "",
		search = "",
		handleChangeCheckbox = () => {},
		handleChangeAreaCheckbox = () => {},
		intersectionRef = null,
		handleChangeSearchResultsCount = () => {},
	} = props;

	const { root, nodes = [] } = getSearchCriteria(accessType) || {};
	const groups = useMemo(() => groupsBySearchCriteria(data, { root, nodes, search }), [data, root, nodes, search]);
	const groupsLength = groups.length;

	useEffect(() => {
		let searchResultsCount = 0;
		groups.forEach(({ filteredOut, list }) => {
			if (!filteredOut) {
				searchResultsCount += list.filter((item) => !item.filteredOut).length;
			}
		});
		handleChangeSearchResultsCount(searchResultsCount);
	}, [groups]);

	if (!root) {
		return (
			<Grid container alignContent={"flex-start"} spacing={2}>
				{groups[0]?.list.map(({ filteredOut, ...item }, key) => {
					const isDisabled = selections.includes(item._id);
					const isChecked = checked.includes(item._id);
					const itemKey = `actions_item_${accessType}_${item._id}_${key}`;
					return (
						<Grid key={itemKey} item xs={6} md={4} className={filteredOut ? "hidden" : ""}>
							<AccessListsItem
								intersectionRef={intersectionRef}
								index={key}
								item={item}
								filteredOut={filteredOut}
								identifier={"_id"}
								title={titleField}
								content={descriptionField}
								checked={isChecked}
								disabled={isDisabled}
								onActionClick={handleChangeCheckbox}
								onActionSelectCheckbox={handleChangeCheckbox}
							/>
						</Grid>
					);
				})}
			</Grid>
		);
	}

	return (
		<Grid container>
			{groups.map(({ filteredOut, ...item }, key) => {
				const itemKey = `group_area_${accessType}_${key}`;
				return (
					<React.Fragment key={itemKey}>
						<RenderListItems
							intersectionRef={intersectionRef}
							filteredOut={filteredOut}
							key={itemKey}
							title={item.title}
							data={item?.list || []}
							titleField={titleField}
							descriptionField={descriptionField}
							checked={checked}
							selections={selections}
							handleChangeCheckbox={handleChangeCheckbox}
							handleChangeAreaCheckbox={handleChangeAreaCheckbox}
							enableGroupCounter={accessType === "users"}
						/>
						{!filteredOut && groups.some((g, idx) => idx > key && key < groupsLength - 1 && !g.filteredOut) && (
							<Grid container justifyContent={"center"}>
								<Grid item xs={12}>
									<Divider className="!border-disabled" sx={{ margin: "10px 0px 20px" }} />
								</Grid>
							</Grid>
						)}
					</React.Fragment>
				);
			})}
		</Grid>
	);
}
AccessListsContent.propTypes = {
	data: PropTypes.array,
	checked: PropTypes.array,
	selections: PropTypes.array,
	accessType: PropTypes.string,
	titleField: PropTypes.string,
	descriptionField: PropTypes.string,
	search: PropTypes.string,
	handleChangeCheckbox: PropTypes.func,
	handleChangeAreaCheckbox: PropTypes.func,
	intersectionRef: PropTypes.object,
	handleChangeSearchResultsCount: PropTypes.func,
};
